/**
 * Klient výlohy pro ankety (#29).
 *
 * Vlastní tenký `request` – schválně odděleně od sdíleného `api.ts`, aby modul
 * anket nezasahoval do souboru, na kterém pracuje více lidí. `ShopError` se jen
 * přebírá. Zápis (hlas) nese CSRF hlavičku a cookies stejně jako zbytek výlohy.
 */
import { ShopError } from './api';

export interface PollOption {
  readonly id: string;
  readonly label: string;
}

export interface PublicPoll {
  readonly id: string;
  readonly question: string;
  readonly options: readonly PollOption[];
}

export interface PollResultOption {
  readonly id: string;
  readonly label: string;
  readonly votes: number;
}

export interface PollResults {
  readonly totalVotes: number;
  readonly options: readonly PollResultOption[];
}

/** Odpověď GET /api/shop/poll. `poll` je `null`, když žádná neběží. */
export interface PollState {
  readonly poll: PublicPoll | null;
  readonly hasVoted?: boolean;
  readonly results?: PollResults;
}

export interface VoteResponse {
  readonly ok: boolean;
  readonly counted: boolean;
  readonly results: PollResults;
}

async function request<T>(path: string, options: { method?: string; body?: unknown } = {}): Promise<T> {
  const headers: Record<string, string> = { 'x-requested-with': 'zooshopik-admin' };
  if (options.body !== undefined) headers['content-type'] = 'application/json';

  let response: Response;
  try {
    response = await fetch(path, {
      method: options.method ?? 'GET',
      headers,
      credentials: 'include',
      ...(options.body === undefined ? {} : { body: JSON.stringify(options.body) }),
    });
  } catch {
    throw new ShopError(0, 'network_error', 'Nepodařilo se spojit se serverem.');
  }

  const text = await response.text();
  let parsed: unknown = null;
  if (text !== '') {
    try {
      parsed = JSON.parse(text);
    } catch {
      parsed = null;
    }
  }

  if (!response.ok) {
    const body = (parsed ?? {}) as { error?: string; message?: string };
    throw new ShopError(response.status, body.error ?? 'unknown_error', body.message ?? 'Něco se pokazilo.');
  }

  return parsed as T;
}

export const pollsApi = {
  active: () => request<PollState>('/api/shop/poll'),
  vote: (pollId: string, optionId: string) =>
    request<VoteResponse>(`/api/shop/poll/${pollId}/vote`, { method: 'POST', body: { optionId } }),
};
